import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { readinessService } from '@/services/readiness.service';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

const EnterpriseReadinessPage: React.FC = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const dashQ = useQuery({
    queryKey: ['erpx-dashboard'],
    queryFn: () => readinessService.getDashboard(),
    staleTime: 30_000,
  });
  const idxQ = useQuery({
    queryKey: ['erpx-indexes'],
    queryFn: () => readinessService.getIndexes(),
    staleTime: 120_000,
  });
  const slowQ = useQuery({
    queryKey: ['erpx-slow'],
    queryFn: () => readinessService.getSlowQueries(),
    staleTime: 60_000,
  });
  const latQ = useQuery({
    queryKey: ['erpx-latency'],
    queryFn: () => readinessService.getApiLatency(),
    staleTime: 30_000,
  });
  const scaleQ = useQuery({
    queryKey: ['erpx-scale'],
    queryFn: () => readinessService.getScale(),
    staleTime: 60_000,
  });
  const secQ = useQuery({
    queryKey: ['erpx-security'],
    queryFn: () => readinessService.getSecurity(),
    staleTime: 30_000,
  });
  const sloQ = useQuery({
    queryKey: ['erpx-slos'],
    queryFn: () => readinessService.getSlos(),
    staleTime: 60_000,
  });
  const compQ = useQuery({
    queryKey: ['erpx-compliance'],
    queryFn: () => readinessService.getCompliance(),
    staleTime: 120_000,
  });
  const runtimeQ = useQuery({
    queryKey: ['erpx-runtime'],
    queryFn: () => readinessService.getRuntime(),
    staleTime: 120_000,
  });
  const drQ = useQuery({
    queryKey: ['erpx-dr'],
    queryFn: () => readinessService.getDr(),
    staleTime: 60_000,
  });
  const devopsQ = useQuery({
    queryKey: ['erpx-devops'],
    queryFn: () => readinessService.getDevops(),
    staleTime: 60_000,
  });
  const intQ = useQuery({
    queryKey: ['erpx-integrations'],
    queryFn: () => readinessService.getIntegrations(),
    staleTime: 120_000,
  });
  const aiQ = useQuery({
    queryKey: ['erpx-ai-perf'],
    queryFn: () => readinessService.getAiPerf(),
    staleTime: 60_000,
  });
  const csQ = useQuery({
    queryKey: ['erpx-cs'],
    queryFn: () => readinessService.getCustomerSuccess(),
    staleTime: 60_000,
  });
  const benchQ = useQuery({
    queryKey: ['erpx-bench'],
    queryFn: () => readinessService.getBenchmarking(),
    staleTime: 120_000,
  });
  const searchQ = useQuery({
    queryKey: ['erpx-page-search', search],
    queryFn: () => readinessService.search(search),
    enabled: search.trim().length >= 2,
    staleTime: 15_000,
    retry: false,
  });

  const reportMut = useMutation({
    mutationFn: () =>
      readinessService.enqueueReport({ reportKind: 'readiness_pack', format: 'pdf' }),
    onSuccess: () => {
      message.success('Report job queued (background)');
      void qc.invalidateQueries({ queryKey: ['erpx-dashboard'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const streams = ((dashQ.data as any)?.streams || {}) as Record<string, any>;
  const recs = (((idxQ.data as any)?.recommendations as any[]) || []).slice(0, 200);
  const latencyRows = ((latQ.data as any)?.rows as any[]) || [];
  const controls = (((compQ.data as any)?.controls as any[]) || []);

  return (
    <OiPageFrame
      subtitle="ERP-X Enterprise Readiness — Streams A–L MVP. Advisory only; never auto-applies DDL, failovers, or ERP mutations."
      loading={dashQ.isLoading && !dashQ.data}
      error={Boolean(dashQ.error)}
      errorMessage="Readiness dashboard unavailable"
    >
      {(dashQ.data as any)?.degraded ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          ERP-X degraded (`ERPX_JOBS_DISABLED=1`).
        </Card>
      ) : null}

      <Space style={{ marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          Enterprise Readiness
        </Title>
        <Link to="/owner-intelligence">OI Home</Link>
        <Button loading={reportMut.isPending} onClick={() => reportMut.mutate()}>
          Queue report job
        </Button>
      </Space>

      {dashQ.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {Object.entries(streams).map(([k, v]) => (
            <Col xs={12} md={8} lg={4} key={k}>
              <PrimaryKpiCard label={k.replace(/_/g, ' ')} value={v?.score ?? '—'} />
            </Col>
          ))}
        </Row>
      )}

      <Tabs
        items={[
          {
            key: 'perf',
            label: 'A Performance',
            children: (
              <>
                <Card title="API latency (P95 target 200ms)" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.method}-${r.route_pattern}-${r.window_start}`}
                    dataSource={latencyRows}
                    pagination={{ pageSize: 10 }}
                    virtual
                    scroll={{ y: 320 }}
                    columns={[
                      { title: 'Method', dataIndex: 'method', width: 80 },
                      { title: 'Route', dataIndex: 'route_pattern' },
                      { title: 'P50', dataIndex: 'p50_ms', width: 80 },
                      { title: 'P95', dataIndex: 'p95_ms', width: 80 },
                      { title: 'P99', dataIndex: 'p99_ms', width: 80 },
                      { title: 'N', dataIndex: 'sample_count', width: 60 },
                    ]}
                    locale={{
                      emptyText: (
                        <OiEmptyState
                          title="No latency samples yet"
                          body="Traffic will populate samples via ERP-X middleware."
                        />
                      ),
                    }}
                  />
                </Card>
                <Card title="Index recommendations (advisory)" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    rowKey={(_, i) => String(i)}
                    dataSource={recs}
                    pagination={{ pageSize: 8 }}
                    virtual
                    scroll={{ y: 280 }}
                    columns={[
                      { title: 'Kind', dataIndex: 'kind', render: (v) => <Tag>{v}</Tag> },
                      { title: 'Table', dataIndex: 'table' },
                      { title: 'Title', dataIndex: 'title' },
                    ]}
                  />
                </Card>
                <Card title="Slow queries">
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    dataSource={(slowQ.data as any[]) || []}
                    columns={[
                      { title: 'ms', dataIndex: 'duration_ms', width: 80 },
                      { title: 'Fingerprint', dataIndex: 'sql_fingerprint' },
                      { title: 'When', dataIndex: 'occurred_at' },
                    ]}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'scale',
            label: 'B Scale',
            children: (
              <Card title="Scale status">
                <Paragraph>
                  Redis: {String((scaleQ.data as any)?.redis?.note || '—')}
                </Paragraph>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => r.item}
                  dataSource={((scaleQ.data as any)?.multiNodeChecklist as any[]) || []}
                  columns={[
                    { title: 'Item', dataIndex: 'item' },
                    { title: 'Status', dataIndex: 'status', render: (s) => <Tag>{s}</Tag> },
                    { title: 'Note', dataIndex: 'note' },
                  ]}
                />
                <Paragraph style={{ marginTop: 12 }}>
                  Storage:{' '}
                  {(((scaleQ.data as any)?.storageAdapters as any[]) || [])
                    .map((a) => `${a.provider}${a.enabled ? '*' : ''}`)
                    .join(', ')}
                </Paragraph>
              </Card>
            ),
          },
          {
            key: 'ux',
            label: 'C UX',
            children: (
              <Card title="NOBLEX Design System + Search Everywhere">
                <Paragraph>
                  Workspace pattern: Header → KPIs → Workspace → Context Sidebar → Timeline →
                  Actions. Tokens live in <Text code>noble-tokens</Text> / <Text code>--noble-*</Text>.
                </Paragraph>
                <Paragraph>
                  Press <Text keyboard>Ctrl+K</Text> for command palette (menus + document search).
                </Paragraph>
                <Space.Compact style={{ width: '100%', maxWidth: 480 }}>
                  <Input
                    placeholder="Search customer / supplier / PO / invoice"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Space.Compact>
                <Table
                  style={{ marginTop: 12 }}
                  size="small"
                  rowKey={(r) => `${r.type}-${r.id}`}
                  dataSource={((searchQ.data as any)?.results as any[]) || []}
                  columns={[
                    { title: 'Type', dataIndex: 'type' },
                    { title: 'Label', dataIndex: 'label' },
                    { title: 'Path', dataIndex: 'path' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'security',
            label: 'D Security',
            children: (
              <>
                <Card title="Security events" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    dataSource={((secQ.data as any)?.events as any[]) || []}
                    columns={[
                      { title: 'Kind', dataIndex: 'event_kind' },
                      { title: 'Severity', dataIndex: 'severity' },
                      { title: 'User', dataIndex: 'user_id' },
                      { title: 'When', dataIndex: 'created_at' },
                    ]}
                  />
                </Card>
                <Card title="Risk users (failed logins ≥ 3)">
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    dataSource={((secQ.data as any)?.riskUsers as any[]) || []}
                    columns={[
                      { title: 'Email', dataIndex: 'email' },
                      { title: 'Attempts', dataIndex: 'failed_login_attempts' },
                      { title: 'Locked', dataIndex: 'is_locked' },
                    ]}
                  />
                </Card>
                <Paragraph type="secondary" style={{ marginTop: 8 }}>
                  SSO/SAML/OIDC/SCIM stubs — see docs. MFA: <Text code>POST /auth/mfa/setup</Text>{' '}
                  (enforce off by default).
                </Paragraph>
              </>
            ),
          },
          {
            key: 'obs',
            label: 'E Observability',
            children: (
              <Card title="SLOs">
                <Table
                  size="small"
                  rowKey={(r) => String(r.id || r.code)}
                  dataSource={(sloQ.data as any[]) || []}
                  columns={[
                    { title: 'Code', dataIndex: 'code' },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Target', dataIndex: 'target_value' },
                    { title: 'Current', dataIndex: 'current_value' },
                    { title: 'Status', dataIndex: 'status', render: (s) => <Tag>{s}</Tag> },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'compliance',
            label: 'F Compliance',
            children: (
              <Card title="Compliance control registry">
                <Table
                  size="small"
                  rowKey={(r) => String(r.id)}
                  dataSource={controls}
                  virtual
                  scroll={{ y: 400 }}
                  columns={[
                    { title: 'Framework', dataIndex: 'framework', width: 120 },
                    { title: 'Code', dataIndex: 'control_code', width: 120 },
                    { title: 'Title', dataIndex: 'title' },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      width: 120,
                      render: (s) => <Tag>{s}</Tag>,
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'dr',
            label: 'G DR',
            children: (
              <Card title={`Disaster Recovery — score ${String((drQ.data as any)?.score ?? '—')}`}>
                <Paragraph>{String((drQ.data as any)?.summary || '')}</Paragraph>
                <Text type="secondary">
                  Targets RPO {(drQ.data as any)?.targets?.rpo_minutes}m / RTO{' '}
                  {(drQ.data as any)?.targets?.rto_minutes}m
                </Text>
                <Table
                  style={{ marginTop: 12 }}
                  size="small"
                  pagination={false}
                  rowKey={(r) => String(r.check_key)}
                  dataSource={((drQ.data as any)?.checks as any[]) || []}
                  columns={[
                    { title: 'Check', dataIndex: 'check_key', width: 160 },
                    { title: 'Status', dataIndex: 'status', width: 100, render: (s) => <Tag>{s}</Tag> },
                    { title: 'Notes', dataIndex: 'notes' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'devops',
            label: 'H DevOps',
            children: (
              <>
                <Card title="Quality gates" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(r) => String(r.gate_key)}
                    dataSource={((devopsQ.data as any)?.gates as any[]) || []}
                    columns={[
                      { title: 'Gate', dataIndex: 'gate_key', width: 140 },
                      { title: 'Status', dataIndex: 'status', width: 100, render: (s) => <Tag>{s}</Tag> },
                      { title: 'Notes', dataIndex: 'notes' },
                    ]}
                  />
                </Card>
                <Card title="Feature flags">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(r) => String(r.flag_key)}
                    dataSource={((devopsQ.data as any)?.featureFlags as any[]) || []}
                    columns={[
                      { title: 'Flag', dataIndex: 'flag_key' },
                      {
                        title: 'On',
                        dataIndex: 'enabled',
                        width: 80,
                        render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'yes' : 'no'}</Tag>,
                      },
                      { title: 'Rollout %', dataIndex: 'rollout_pct', width: 100 },
                      { title: 'Description', dataIndex: 'description' },
                    ]}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'integrations',
            label: 'I Integration',
            children: (
              <Card
                title={`Connector catalog — ${String((intQ.data as any)?.live ?? 0)} live / ${String(
                  (intQ.data as any)?.stub ?? 0
                )} stub`}
              >
                <Paragraph type="secondary">{String((intQ.data as any)?.note || '')}</Paragraph>
                <Table
                  size="small"
                  rowKey={(r) => String(r.connector_key)}
                  dataSource={((intQ.data as any)?.connectors as any[]) || []}
                  columns={[
                    { title: 'Connector', dataIndex: 'connector_key' },
                    { title: 'Category', dataIndex: 'category', width: 120 },
                    { title: 'Status', dataIndex: 'status', width: 100, render: (s) => <Tag>{s}</Tag> },
                    { title: 'Health', dataIndex: 'health', width: 100 },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'ai',
            label: 'J AI Perf',
            children: (
              <Card title="AI latency / cost (advisory)">
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => String(r.model_key)}
                  dataSource={((aiQ.data as any)?.models as any[]) || []}
                  columns={[
                    { title: 'Model', dataIndex: 'model_key' },
                    { title: 'Calls', dataIndex: 'calls', width: 80 },
                    { title: 'Avg ms', dataIndex: 'avg_latency_ms', width: 90 },
                    { title: 'Tokens', dataIndex: 'tokens', width: 90 },
                    { title: 'Cost USD', dataIndex: 'cost_usd', width: 100 },
                    { title: 'Cache %', dataIndex: 'cache_hit_pct', width: 90 },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'cs',
            label: 'K CS',
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={12} md={6}>
                  <PrimaryKpiCard label="Health" value={(csQ.data as any)?.health_score ?? '—'} />
                </Col>
                <Col xs={12} md={6}>
                  <PrimaryKpiCard label="Adoption %" value={(csQ.data as any)?.adoption_pct ?? '—'} />
                </Col>
                <Col xs={12} md={6}>
                  <PrimaryKpiCard label="Active 30d" value={(csQ.data as any)?.active_users_30d ?? '—'} />
                </Col>
                <Col xs={12} md={6}>
                  <PrimaryKpiCard
                    label="Upgrade ready"
                    value={(csQ.data as any)?.upgrade_ready ? 'Yes' : 'No'}
                  />
                </Col>
              </Row>
            ),
          },
          {
            key: 'bench',
            label: 'L Cert',
            children: (
              <>
                <Card title="Certification gates" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(r) => String(r.gate_key)}
                    dataSource={((benchQ.data as any)?.certificationGates as any[]) || []}
                    columns={[
                      { title: 'Gate', dataIndex: 'gate_key', width: 140 },
                      { title: 'Status', dataIndex: 'status', width: 100, render: (s) => <Tag>{s}</Tag> },
                      { title: 'Notes', dataIndex: 'notes' },
                    ]}
                  />
                </Card>
                <Card title="Phase 2 stream status">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(r) => r.id}
                    dataSource={((runtimeQ.data as any)?.roadmapGL as any[]) || []}
                    columns={[
                      { title: 'ID', dataIndex: 'id', width: 60 },
                      { title: 'Title', dataIndex: 'title' },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 80,
                        render: (s) => <Tag color="blue">{s || 'mvp'}</Tag>,
                      },
                      {
                        title: 'Targets',
                        dataIndex: 'targets',
                        render: (t: string[]) => (t || []).join(', '),
                      },
                    ]}
                  />
                </Card>
              </>
            ),
          },
        ]}
      />
    </OiPageFrame>
  );
};

export default EnterpriseReadinessPage;
